import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditableFlashcardCard } from "src/components/NewComponents/Flashcards/EditableFlashcardCard";

import Filter from "../DefaultFilters";

interface Flashcard {
	id?: string;
	front: string;
	back: string;
	key?: number;
}

interface AllFlashcardFilterProps {
	selectedData: { flashcards: Flashcard[] };
	setSelectedData: (data: { flashcards: Flashcard[] }) => void;
	expanded: boolean;
	onChange: () => void;
}

function AllFlashcardFilter(props: AllFlashcardFilterProps) {
	const { t } = useTranslation();
	const [added, setAdded] = useState(false);
	const [editingKey, setEditingKey] = useState<number | null>(null);
	const prevCountRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		const { length } = props.selectedData.flashcards;
		if (prevCountRef.current !== undefined && prevCountRef.current < length) {
			setAdded(true);
			const timeout = setTimeout(() => setAdded(false), 1000);
			prevCountRef.current = length;
			return () => clearTimeout(timeout);
		}
		prevCountRef.current = length;
		return undefined;
	}, [props.selectedData.flashcards]);

	const saveEdit = (
		flashcard: Flashcard,
		next: { front: string; back: string },
	) => {
		props.setSelectedData({
			flashcards: props.selectedData.flashcards.map((fc) =>
				fc.key === flashcard.key
					? { ...fc, front: next.front, back: next.back }
					: fc,
			),
		});
		setEditingKey(null);
	};

	const onDelete = (flashcard: Flashcard) => {
		const flashcards = props.selectedData.flashcards.filter(
			(f) => f.key !== flashcard.key,
		);

		props.setSelectedData({
			flashcards: flashcards.map((fc, key) => ({ ...fc, key })),
		});
	};

	const renderFilters = () => {
		if (props.selectedData.flashcards.length === 0) {
			return <span>{t("errors.emptyDeck")}</span>;
		}

		return (
			<div className="flashcards-list">
				{props.selectedData.flashcards.map((flashcard) => (
					<EditableFlashcardCard
						key={flashcard.key}
						flashcard={flashcard}
						isEditing={editingKey !== null && flashcard.key === editingKey}
						onStartEdit={() => {
							if (flashcard.key !== undefined) {
								setEditingKey(flashcard.key);
							}
						}}
						onCancelEdit={() => setEditingKey(null)}
						onSave={(next) => saveEdit(flashcard, next)}
						onDelete={() => onDelete(flashcard)}
						disableEdit={!!flashcard.id}
						disableEditMessage={t("errors.editPrivateFlashcards")}
					/>
				))}
			</div>
		);
	};

	return (
		<Filter
			expanded={props.expanded}
			onChange={props.onChange}
			title={
				<span>
					{t("creators.decks.counterText")}:{" "}
					{props.selectedData.flashcards.length}{" "}
					{added ? (
						<span className="text-success">{t("commands.added")}</span>
					) : (
						""
					)}
				</span>
			}
			renderFilterDetails={renderFilters}
		/>
	);
}

export default AllFlashcardFilter;
